import Icon from "../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AddIcon,
  Button,
  Card,
  type ContextMenuAction,
  CopyIcon,
  EditIcon,
  FlexContent,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import Table from "../../components/ui/Table";
import { getDeleteAction } from "../../utils/rowActions";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchCalculation } from "../../http/types/calculations";
import { useCalculationsApi } from "../../http/useCalculations";
import { PaginationQuery } from "../../types";
import { formatTimeStamp } from "../../utils";
import { CalculationForm } from "../calculations/CalculationForm";
import DuplicateCalculationDialog from "../calculations/DuplicateCalculationDialog";

interface ProjectCalculationsProps {
  projectId: string;
  isAdmin?: boolean;
  disabled?: boolean;
  canEdit?: boolean;
}

const ProjectCalculations: FC<ProjectCalculationsProps> = ({
  projectId,
  isAdmin,
  disabled,
  canEdit,
}: ProjectCalculationsProps) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const calculationsApi = useCalculationsApi();

  const query: PaginationQuery = {
    ...filters.state,
  };

  const { data, error } = calculationsApi.useProjectCalculations(projectId, query, aborter.current.signal, isAdmin);

  const [calculationToDelete, setCalculationToDelete] = useState<SearchCalculation>();
  const [calculationToDuplicate, setCalculationToDuplicate] = useState<SearchCalculation>();
  const [showModalCreateCalculation, setShowModalCreateCalculation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isInitialized = useInitializer(data || error);

  if (!data && !error && !isInitialized) {
    return;
  }

  const urlCalculations = isAdmin ? "/admin/calculations" : "/my-calculations";
  const calculationDetailsUrl = (calculationId: string) => `${urlCalculations}/${calculationId}?projectId=${projectId}`;

  const rowActions: (item: SearchCalculation) => ContextMenuAction[] = (item) => [
    {
      label: item.canEdit ? i18n.translation.common.edit : i18n.translation.common.open,
      onClick: () => navigate(calculationDetailsUrl(item.id)),
      icon: <Icon glyph={EditIcon} />,
    },
    {
      label: i18n.translation.common.duplicate,
      onClick: () => {
        (isAdmin || item.canEdit) && setCalculationToDuplicate(item);
        return !(isAdmin || item.canEdit);
      },
      disabled: !isAdmin && !item.canEdit,
      icon: <Icon glyph={CopyIcon} />,
    },
    getDeleteAction(
      i18n,
      () => {
        (isAdmin || item.canEdit) && setCalculationToDelete(item);
        return !(isAdmin || item.canEdit);
      },
      true,
      !isAdmin && !item.canEdit,
    ),
  ];

  const canAdd = isAdmin || canEdit || (data?.items?.[0]?.canEdit ?? false);
  const buttons = [
    <Button disabled={!canAdd} onClick={() => setShowModalCreateCalculation(true)}><Icon glyph={AddIcon} />
      {i18n.translation.calculations.createNew}
    </Button>,
  ];

  return (
    <>
      <FlexContent padding>
        <Card shadow>
          <Table<SearchCalculation>
            filters={filters}
            search={{
              disabled: disabled,
              placeholder: i18n.translation.calculations.search,
              onChange: () => {
                aborter.current.abort();
                aborter.current = new AbortController();
              },
            }}
            headingActions={buttons}
            head={
              <TableRow>
                <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
                <TableHeadCell align="left">{i18n.translation.modules.singular}</TableHeadCell>
                <TableHeadCell align="left">{i18n.translation.common.owner}</TableHeadCell>
                <TableHeadCell align="left">{i18n.translation.common.lastModified}</TableHeadCell>
                <TableHeadCell></TableHeadCell>
              </TableRow>
            }
            items={data?.items}
            renderRow={(item) => (
              <>
                <TableCell>
                  <Link to={calculationDetailsUrl(item.id)} onClick={(e) => e.stopPropagation()}>
                    {item.name}
                  </Link>
                </TableCell>
                <TableCell>{item.module?.name}</TableCell>
                <TableCell>
                  {item.owner?.firstName} {item.owner?.lastName}
                </TableCell>
                <TableCell>{formatTimeStamp(item.modifiedAt, auth.identity?.language)}</TableCell>
              </>
            )}
            onRowClick={(item) => navigate(calculationDetailsUrl(item.id))}
            rowActions={rowActions}
            total={data?.total}
            error={!!error}
          />
          {!!calculationToDelete && (
            <ModalDialog
              onClose={() => setCalculationToDelete(undefined)}
              title={i18n.translation.calculations.delete}
              isSubmitting={isSubmitting}
              buttons={[<Button key={0} onClick={() => setCalculationToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                    setIsSubmitting(true);
                    calculationsApi
                      .deleteCalculation(projectId, calculationToDelete.id, isAdmin)
                      .then(() => setCalculationToDelete(undefined))
                      .catch((err) => dispatch(showErrorNotification(i18n, err)))
                      .finally(() => setIsSubmitting(false));
                  }}>{i18n.translation.common.yes}</Button>]}
            >
              {i18n.translation.calculations.questions.delete}
            </ModalDialog>
          )}
          {!!calculationToDuplicate && (
            <DuplicateCalculationDialog
              calculation={calculationToDuplicate}
              onClose={() => setCalculationToDuplicate(undefined)}
              onSave={() => setCalculationToDuplicate(undefined)}
              isAdmin={isAdmin}
            />
          )}
        </Card>
      </FlexContent>

      {showModalCreateCalculation && (
        <ModalDialog
          onClose={() => {
            setShowModalCreateCalculation(false);
          }}
          title={i18n.translation.calculations.createNew}
          isSubmitting={isSubmitting}
          buttons={[]}
        >
          <CalculationForm
            isAdmin={isAdmin}
            projectId={projectId}
            onSuccess={() => {
              setShowModalCreateCalculation(false);
            }}
            onClose={() => setShowModalCreateCalculation(false)}
          />
        </ModalDialog>
      )}
    </>
  );
};

export default ProjectCalculations;
